// Voucher detail — header + items + journal projection + Print button.
// Phase 2.1: read-only. Edit/clone flows ship Phase 2.2.

import { useEffect, useState } from 'react';
import { useVoucher, useCancelVoucher, voucherPdfUrl } from '@/api/vouchers';
import { Money } from '@/components/primitives/Money';
import { Button } from '@/components/primitives/Button';
import { KbdChip } from '@/components/primitives/KbdChip';
import { PageHeader } from '@/components/primitives/PageHeader';
import { cleanErr } from '@/lib/errMsg';
import { formatDateDDMMMYYYY, displayNarration } from '@/utils/format';
import { cn } from '@/lib/cn';

function getVoucherIdFromPath(): string | null {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/vouchers\/([^/]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export function VoucherDetailPage() {
  const [id, setId] = useState<string | null>(() => getVoucherIdFromPath());
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    function onPop() { setId(getVoucherIdFromPath()); }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const v = useVoucher(id);
  const cancel = useCancelVoucher();

  // Ctrl+P fires the Raaj Classic PDF link via the global edit.print handler,
  // which clicks the element tagged data-shortcut="print" below.

  if (!id) {
    return <div className="p-10 text-center text-slate">No voucher selected.</div>;
  }

  if (v.isLoading) {
    return (
      <div className="min-h-screen bg-snow">
        <header className="border-b border-mist bg-snow/95">
          <div className="mx-auto max-w-[1280px] px-4 py-4 sm:px-6 space-y-2">
            <div className="h-4 w-20 bg-frost rounded-sm animate-pulse" />
            <div className="h-9 w-80 bg-frost rounded-sm animate-pulse" />
            <div className="h-3 w-56 bg-frost rounded-sm animate-pulse" />
          </div>
        </header>
        <main className="mx-auto max-w-[1280px] px-4 py-8 sm:px-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-24 bg-frost rounded-md animate-pulse" />)}
          </div>
          <div className="h-72 bg-frost rounded-md animate-pulse" />
        </main>
      </div>
    );
  }

  if (v.error || !v.data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-snow p-6 text-center">
        <h2 className="font-display text-display-md text-obsidian">Voucher not found</h2>
        <p className="text-body-md text-slate">{cleanErr(v.error, 'The voucher you requested does not exist.')}</p>
        <a href="/vouchers" className="text-cobalt hover:underline">← Back to Vouchers</a>
      </div>
    );
  }

  const d = v.data;
  const dateStr = formatDateDDMMMYYYY(new Date(d.date));

  return (
    <div className="min-h-screen bg-snow">
      {/* Header — sticky */}
      <PageHeader
        backHref="/vouchers"
        backLabel="Vouchers"
        title={
          <span className="inline-flex flex-wrap items-baseline gap-2">
            {d.voucherType?.name ?? 'Voucher'}
            <span className="font-mono text-body-md text-cobalt">{d.number}</span>
          </span>
        }
        subtitle={
          <>
            {dateStr}
            {d.partyLedger && <> · <span className="text-obsidian">{d.partyLedger.name}</span></>}
            {d.cancelledAt && <span className="ml-2 inline-flex items-center h-5 px-2 rounded-sm bg-crimson/10 text-crimson text-nano font-medium uppercase tracking-[0.1em]">Cancelled</span>}
            {d.tallySyncedAt && <span className="ml-2 inline-flex items-center h-5 px-2 rounded-sm bg-emerald/10 text-emerald text-nano font-medium uppercase tracking-[0.1em]" title={`Synced to Tally at ${d.tallySyncedAt}`}>Tally ✓</span>}
            {d.irn && <span className="ml-2 inline-flex items-center h-5 px-2 rounded-sm bg-cobalt/10 text-cobalt text-nano font-medium uppercase tracking-[0.1em]" title={`IRN: ${d.irn}`}>e-Invoice ✓</span>}
          </>
        }
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2 print:hidden">
            <a
              href={voucherPdfUrl(d.id, 'raaj-classic')}
              target="_blank"
              rel="noopener noreferrer"
              data-shortcut="print"
              className="inline-flex items-center gap-2 rounded-md bg-cobalt px-3 py-1.5 text-body-sm font-medium text-snow hover:bg-cobalt/40"
              title="Raaj Classic — pixel-faithful to the legacy Sadaf invoice layout"
            >
              <span className="text-base leading-none">⤓</span> Raaj Classic
              <KbdChip className="bg-snow/15 text-snow border-snow/20">Ctrl P</KbdChip>
            </a>
            <a
              href={voucherPdfUrl(d.id, 'tally-simple')}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md border border-cobalt/40 bg-frost px-3 py-1.5 text-body-sm text-cobalt hover:bg-cobalt/[0.05]"
              title="Tally Simple — monochrome A4 invoice that mirrors Tally Prime print output"
            >
              Tally Simple
            </a>
            {['SALES', 'CREDIT_NOTE', 'DEBIT_NOTE'].includes(d.voucherType?.baseTypeCode ?? '') && (
              <a
                href={`/api/vouchers/${d.id}/e-invoice/json?download=true`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md border border-mist bg-haze px-3 py-1.5 text-body-sm text-obsidian hover:border-cobalt"
                title="Download GST-Council e-Invoice JSON for offline upload"
              >
                <span className="text-base leading-none">⌬</span> e-Invoice JSON
              </a>
            )}
            {['SALES', 'PURCHASE', 'CREDIT_NOTE', 'DEBIT_NOTE'].includes(d.voucherType?.baseTypeCode ?? '') && (
              <a
                href={`/api/vouchers/${d.id}/e-way-bill/json?download=true`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md border border-mist bg-haze px-3 py-1.5 text-body-sm text-obsidian hover:border-citrus"
                title="Download NIC e-Way Bill (EWB-01) JSON for portal upload — required when consignment > ₹50,000"
              >
                <span className="text-base leading-none">⛟</span> e-Way Bill JSON
              </a>
            )}
            <a
              href="/admin/tally-outbox"
              className="hidden lg:inline-flex items-center gap-2 rounded-md border border-mist bg-haze px-3 py-1.5 text-body-sm text-obsidian hover:border-cobalt"
              title="View this voucher's Tally sync status in the outbox queue"
            >
              <span className="text-base leading-none">⇄</span> Tally Outbox
            </a>
            {(() => {
              // Clone path matches the voucher's base type → opens the matching New form pre-filled.
              const base = d.voucherType?.baseTypeCode;
              const clonePathByBase: Record<string, string> = {
                SALES: '/vouchers/new/sales',
                PURCHASE: '/vouchers/new/purchase',
                RECEIPT: '/vouchers/new/receipt',
                PAYMENT: '/vouchers/new/payment',
                CONTRA: '/vouchers/new/contra',
                JOURNAL: '/vouchers/new/journal',
                CREDIT_NOTE: '/vouchers/new/credit-note',
                DEBIT_NOTE: '/vouchers/new/debit-note',
              };
              const cloneHref = base && clonePathByBase[base] ? `${clonePathByBase[base]}?cloneFrom=${d.id}` : null;
              return cloneHref ? (
                <a
                  href={cloneHref}
                  className="inline-flex items-center gap-2 rounded-md border border-cobalt/40 bg-frost px-3 py-1.5 text-body-sm text-cobalt hover:bg-cobalt/[0.05]"
                  title="Duplicate this voucher as the starting point for a new one"
                >
                  <span className="text-base leading-none">⎘</span> Clone
                </a>
              ) : null;
            })()}
            <Button variant="ghost" onClick={() => window.print()} title="Browser-native print of this voucher detail">Print</Button>
            {!d.cancelledAt && (
              <Button variant="ghost" onClick={() => { setCancelReason(''); setCancelOpen(true); }}
                className="border border-crimson/40 text-crimson hover:bg-crimson/[0.05]">
                Cancel voucher
              </Button>
            )}
          </div>
        }
      />

      <main className="mx-auto max-w-[1280px] px-4 py-6 sm:px-6 sm:py-8">
        {/* Totals strip */}
        <section className="orz-stagger mb-8 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          <Card label="Taxable value"><Money amount={Number(d.subTotal)} className="text-display-md" /></Card>
          <Card label="Tax (CGST + SGST + IGST + CESS)"><Money amount={Number(d.totalTaxAmount)} className="text-display-md text-cool" /></Card>
          <Card label="Round-off"><Money amount={Number(d.roundOff)} className="text-display-md text-cool" /></Card>
          <Card label="Invoice total" highlight><Money amount={Number(d.totalAmount)} important className="text-display-md" /></Card>
        </section>

        {d.narration && (
          <section className="mb-6 rounded-md border border-mist bg-frost p-4">
            <div className="text-micro font-medium uppercase tracking-[0.12em] text-cool">Narration</div>
            <p className="mt-1 text-body-md text-obsidian">{displayNarration(d.narration)}</p>
          </section>
        )}

        {/* Line items */}
        <section className="mb-8">
          <h2 className="mb-3 text-micro font-medium uppercase tracking-[0.12em] text-cool">
            <span className="mr-2 inline-block h-[1px] w-4 bg-cobalt align-middle" />
            Line items ({d.lines.length})
          </h2>
          <div className="overflow-hidden rounded-md border border-mist bg-frost">
            <div className="overflow-x-auto">
              <div className="grid min-w-max grid-cols-[36px_minmax(240px,2fr)_60px_80px_80px_100px_100px_100px] gap-x-3 border-b border-mist bg-frost px-4 py-2 text-micro font-medium uppercase tracking-[0.12em] text-cool">
                <div>#</div>
                <div>Item / Ledger / Description</div>
                <div className="text-right">HSN</div>
                <div className="text-right">Qty</div>
                <div className="text-right">Rate</div>
                <div className="text-right">Taxable</div>
                <div className="text-right">Tax</div>
                <div className="text-right">Total</div>
              </div>
              {d.lines.map((l) => (
                <div
                  key={l.id}
                  className="grid min-w-max grid-cols-[36px_minmax(240px,2fr)_60px_80px_80px_100px_100px_100px] gap-x-3 border-b border-mist px-4 py-2 text-body-sm text-obsidian"
                >
                  <div className="font-mono text-caption text-cool">{l.lineNumber}</div>
                  <div className="flex flex-col">
                    <span>{l.item?.name ?? l.ledger?.name ?? '—'}</span>
                    {l.description && <span className="text-caption text-cool">{l.description}</span>}
                  </div>
                  <div className="text-right font-mono text-caption text-cool">{l.hsnSac?.code ?? '—'}</div>
                  <div className="text-right font-mono tabular-nums">{l.quantityPrimary != null ? Number(l.quantityPrimary) : '—'}</div>
                  <div className="text-right font-mono tabular-nums">{l.ratePerUnit != null ? Number(l.ratePerUnit).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</div>
                  <div className="text-right font-mono tabular-nums">{l.taxableValue != null ? Number(l.taxableValue).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</div>
                  <div className="text-right font-mono tabular-nums text-cool">
                    {((Number(l.cgstAmount ?? 0) + Number(l.sgstAmount ?? 0) + Number(l.igstAmount ?? 0) + Number(l.cessAmount ?? 0)) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="text-right font-mono tabular-nums">{l.totalAmount != null ? Number(l.totalAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Journal lines */}
        <section className="mb-8">
          <h2 className="mb-3 text-micro font-medium uppercase tracking-[0.12em] text-cool">
            <span className="mr-2 inline-block h-[1px] w-4 bg-cobalt align-middle" />
            Journal projection (double-entry)
          </h2>
          <div className="overflow-hidden rounded-md border border-mist bg-frost">
            <div className="grid grid-cols-[1fr_140px_140px] gap-x-3 border-b border-mist bg-frost px-4 py-2 text-micro font-medium uppercase tracking-[0.12em] text-cool">
              <div>Ledger</div>
              <div className="text-right">Debit</div>
              <div className="text-right">Credit</div>
            </div>
            {d.journalLines.map((jl) => {
              const amt = Number(jl.amount);
              return (
                <div key={jl.id} className="grid grid-cols-[1fr_140px_140px] gap-x-3 border-b border-mist px-4 py-2 text-body-sm">
                  <div>
                    <span className="text-obsidian">{jl.ledger?.name ?? '—'}</span>
                    {jl.narration && <span className="ml-2 text-caption text-cool">· {displayNarration(jl.narration)}</span>}
                  </div>
                  <div className={cn('text-right font-mono tabular-nums', amt > 0 ? 'text-obsidian' : 'text-cool')}>
                    {amt > 0 ? amt.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                  </div>
                  <div className={cn('text-right font-mono tabular-nums', amt < 0 ? 'text-obsidian' : 'text-cool')}>
                    {amt < 0 ? Math.abs(amt).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                  </div>
                </div>
              );
            })}
            {/* Totals + balance check */}
            {(() => {
              const dr = d.journalLines.reduce((s, l) => s + Math.max(0, Number(l.amount)), 0);
              const cr = d.journalLines.reduce((s, l) => s + Math.max(0, -Number(l.amount)), 0);
              const balanced = Math.abs(dr - cr) < 0.01;
              return (
                <div className="grid grid-cols-[1fr_140px_140px] gap-x-3 border-t-2 border-obsidian/40 bg-frost px-4 py-2 text-body-sm font-medium">
                  <div className="flex items-center gap-2 text-cool uppercase tracking-[0.08em] text-micro">
                    Totals · double-entry
                    {balanced
                      ? <span className="inline-flex items-center h-5 px-2 rounded-sm bg-emerald/10 text-emerald text-nano font-medium uppercase tracking-[0.1em]">Dr ≡ Cr ✓</span>
                      : <span className="inline-flex items-center h-5 px-2 rounded-sm bg-crimson/10 text-crimson text-nano font-medium uppercase tracking-[0.1em]">Gap {(dr - cr).toFixed(2)}</span>}
                  </div>
                  <div className="text-right font-mono tabular-nums text-obsidian">{dr.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  <div className="text-right font-mono tabular-nums text-obsidian">{cr.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                </div>
              );
            })()}
          </div>
        </section>

        {/* Bill-wise refs (Tally AR/AP subledger — Phase 4.6 surfaced on detail) */}
        {d.billRefs && d.billRefs.length > 0 && (
          <section className="mb-8">
            <h2 className="mb-3 text-micro font-medium uppercase tracking-[0.12em] text-cool">
              <span className="mr-2 inline-block h-[1px] w-4 bg-citrus align-middle" />
              Bill-wise allocations ({d.billRefs.length})
            </h2>
            <div className="overflow-hidden rounded-md border border-mist bg-frost">
              <div className="grid grid-cols-[100px_minmax(140px,1fr)_120px_120px_140px_140px] gap-x-3 border-b border-mist bg-frost px-4 py-2 text-micro font-medium uppercase tracking-[0.12em] text-cool">
                <div>Type</div>
                <div>Ref name</div>
                <div className="font-mono">Ref date</div>
                <div className="font-mono">Due date</div>
                <div className="text-right">Amount</div>
                <div className="text-right">Settled</div>
              </div>
              {d.billRefs.map((br) => (
                <div key={br.id} className="grid grid-cols-[100px_minmax(140px,1fr)_120px_120px_140px_140px] gap-x-3 border-b border-mist px-4 py-2 text-body-sm">
                  <div>
                    <span className={cn('inline-flex items-center h-5 px-2 rounded-sm text-nano font-medium uppercase tracking-[0.1em]',
                      br.type === 'NEW' && 'bg-cobalt/10 text-cobalt',
                      br.type === 'AGAINST' && 'bg-emerald/10 text-emerald',
                      br.type === 'ADVANCE' && 'bg-cobalt/10 text-cobalt',
                      br.type === 'ON_ACCOUNT' && 'bg-mist text-slate',
                    )}>{br.type}</span>
                  </div>
                  <div className="font-mono text-caption text-cobalt">{br.refName}</div>
                  <div className="font-mono text-caption text-cool">{formatDateDDMMMYYYY(br.refDate)}</div>
                  <div className="font-mono text-caption">{br.dueDate ? formatDateDDMMMYYYY(br.dueDate) : '—'}</div>
                  <div className="text-right font-mono tabular-nums">{Number(br.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                  <div className="text-right font-mono tabular-nums text-cool">{Number(br.settledAmount ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Stock movements (if any) */}
        {d.stockMovements.length > 0 && (
          <section className="mb-8">
            <h2 className="mb-3 text-micro font-medium uppercase tracking-[0.12em] text-cool">
              <span className="mr-2 inline-block h-[1px] w-4 bg-cobalt align-middle" />
              Stock movements ({d.stockMovements.length})
            </h2>
            <div className="overflow-hidden rounded-md border border-mist bg-frost">
              <div className="grid grid-cols-[60px_1fr_100px_120px_140px] gap-x-3 border-b border-mist bg-frost px-4 py-2 text-micro font-medium uppercase tracking-[0.12em] text-cool">
                <div>Dir</div>
                <div>Item</div>
                <div className="text-right">Qty</div>
                <div className="text-right">Rate</div>
                <div className="text-right">Value</div>
              </div>
              {d.stockMovements.map((sm) => (
                <div key={sm.id} className="grid grid-cols-[60px_1fr_100px_120px_140px] gap-x-3 border-b border-mist px-4 py-2 text-body-sm">
                  <div>
                    <span className={cn('inline-flex items-center h-5 px-2 rounded-sm text-nano font-medium uppercase tracking-[0.1em]', sm.direction === 'IN' ? 'bg-emerald/10 text-emerald' : 'bg-slate/10 text-slate')}>
                      {sm.direction}
                    </span>
                  </div>
                  <div>{sm.item?.name ?? '—'}</div>
                  <div className="text-right font-mono tabular-nums">{Number(sm.quantity).toLocaleString('en-IN')}</div>
                  <div className="text-right font-mono tabular-nums text-cool">{Number(sm.ratePerUnit).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</div>
                  <div className="text-right font-mono tabular-nums">{Number(sm.totalValue).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Cancel voucher modal */}
      {cancelOpen && d && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-obsidian/40 p-4" onClick={() => setCancelOpen(false)}>
          <div role="dialog" aria-modal="true" aria-labelledby="cancel-voucher-title" className="w-full max-w-md rounded-md bg-frost p-5 shadow-card" onClick={(e) => e.stopPropagation()}>
            <h3 id="cancel-voucher-title" className="font-display text-display-sm font-semibold text-obsidian">Cancel voucher {d.number}?</h3>
            <p className="mt-1 text-body-sm text-slate">
              This will mark the voucher as cancelled. Append-only ledger lines stay intact; downstream reports
              will exclude this voucher. Audit log captures the reason.
            </p>
            <label className="mt-3 block text-caption font-medium text-obsidian">
              Reason (optional but recommended)
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g. Duplicate entry / Wrong party / Customer cancelled order"
                rows={3}
                className="mt-1 w-full rounded-sm border border-mist bg-haze px-2 py-1 text-body-sm focus:border-cobalt/60 focus:outline-none focus:shadow-focus"
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setCancelOpen(false)}>Keep voucher</Button>
              <Button
                variant="danger"
                disabled={cancel.isPending}
                onClick={() => {
                  cancel.mutate({ id: d.id, reason: cancelReason.trim() || undefined }, {
                    onSuccess: () => setCancelOpen(false),
                  });
                }}
              >
                {cancel.isPending ? 'Cancelling…' : 'Cancel voucher'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ label, highlight, children }: { label: string; highlight?: boolean; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'orz-lift rounded-md border p-4 sm:p-5',
        highlight ? 'border-cobalt/40 bg-cobalt/[0.05] shadow-card' : 'border-mist bg-snow shadow-card',
      )}
      style={!highlight ? { backgroundImage: 'var(--wash-card)' } : undefined}
    >
      <div className="text-micro font-medium uppercase tracking-[0.12em] text-cool">{label}</div>
      <div className="mt-2">{children}</div>
    </div>
  );
}
